/**
 * Vanta Manage API payload subsets used by the desktop adapter AND the sim.
 *
 * real-providers change: every object schema is tolerant. A `z.preprocess` step aliases the
 * verified real-API field names (`.context/PROVIDER_API_NOTES.md` section 1.5) onto the field
 * names the normalizers rely on, coerces ids to strings and fills required legacy fields from
 * fallbacks; the object itself is `.loose()` so unknown fields pass through, status fields are
 * plain strings (an unlisted value maps to UNKNOWN downstream, never PASS) and timestamps are
 * tolerant (unparsable values become null instead of dropping the record). The legacy fixture and
 * sim shapes still parse unchanged.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------------------------
// real-providers change: tolerant primitives and aliasing helpers
// ---------------------------------------------------------------------------------------------

type Raw = Record<string, unknown>;

export function isRawObject(v: unknown): v is Raw {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** First value that is neither null nor undefined. */
export function pickFirst(...values: unknown[]): unknown {
  return values.find((v) => v !== undefined && v !== null);
}

/** Ids arrive as strings (Vanta) or numbers (Drata); the record key needs a string. */
export function rawIdString(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return undefined;
}

function toIso(v: unknown): string | null | undefined {
  if (v === null || v === undefined) return v;
  if (typeof v !== 'string') return null;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
}

/** Any Date-parsable string (ISO date-time or date-only) normalised to ISO-8601 UTC; anything else is null. */
export const looseDateTime = z.preprocess(toIso, z.string().nullable().optional());

/** A URL string, or null when it does not parse; never fails the record. */
export const looseUrl = z.preprocess((v) => {
  if (v === null || v === undefined) return v;
  if (typeof v !== 'string') return null;
  try {
    new URL(v);
    return v;
  } catch {
    return null;
  }
}, z.string().nullable().optional());

/** Status-like fields: strings only; absent becomes null so `rawStatus` can retain it. */
const rawStatus = z.string().nullable();

const nullableBool = z.boolean().nullable().optional();

function stringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map(rawIdString).filter((s): s is string => s !== undefined) : [];
}

const isoDateTime = looseDateTime;

export const VantaTokenResponseSchema = z
  .object({
    access_token: z.string().min(1),
    /** Documented "Always Bearer"; accepted case-insensitively. */
    token_type: z.string().optional(),
    expires_in: z.number().int().positive(),
    scope: z.string().optional(),
  })
  .loose();
export type VantaTokenResponse = z.infer<typeof VantaTokenResponseSchema>;

export const VantaTokenRequestSchema = z.object({
  grant_type: z.literal('client_credentials'),
  client_id: z.string().min(1),
  client_secret: z.string().min(1),
  scope: z.string().default('vanta-api.all:read'),
});
export type VantaTokenRequest = z.infer<typeof VantaTokenRequestSchema>;

export const VantaErrorResponseSchema = z
  .object({
    error: z.string().optional(),
    error_description: z.string().optional(),
    message: z.string().optional(),
    statusCode: z.number().int().optional(),
  })
  .loose();
export type VantaErrorResponse = z.infer<typeof VantaErrorResponseSchema>;

/** real-providers change: `hasNextPage` is derived from `endCursor` when the provider omits it. */
export const VantaPageInfoSchema = z.preprocess(
  (v) => {
    if (!isRawObject(v)) return v;
    const endCursor = typeof v.endCursor === 'string' && v.endCursor !== '' ? v.endCursor : null;
    return { ...v, endCursor, hasNextPage: typeof v.hasNextPage === 'boolean' ? v.hasNextPage : endCursor !== null };
  },
  z
    .object({
      endCursor: z.string().nullable(),
      hasNextPage: z.boolean(),
      startCursor: z.string().nullable().optional(),
      hasPreviousPage: z.boolean().optional(),
    })
    .loose(),
);
export type VantaPageInfo = z.infer<typeof VantaPageInfoSchema>;

/** `{ results: { data: T[], pageInfo } }` cursor envelope (`totalCount` optional, seen in examples only). */
export function vantaPage<T extends z.ZodType>(item: T) {
  return z
    .object({
      results: z
        .object({
          data: z.array(item),
          pageInfo: VantaPageInfoSchema,
          totalCount: z.number().int().nonnegative().nullable().optional(),
        })
        .loose(),
    })
    .loose();
}
export type VantaPage<T> = { results: { data: T[]; pageInfo: VantaPageInfo; totalCount?: number | null } };

export const VantaPageQuerySchema = z.object({
  pageCursor: z.string().optional(),
  pageSize: z.coerce.number().int().min(1).max(100).default(100),
});
export type VantaPageQuery = z.infer<typeof VantaPageQuerySchema>;

export const VANTA_TEST_STATUSES = [
  'NEEDS_ATTENTION',
  'OK',
  'DEACTIVATED',
  'NOT_APPLICABLE',
  'IN_PROGRESS',
  'INVALID',
] as const;
export const VantaTestStatusSchema = z.enum(VANTA_TEST_STATUSES);
export type VantaTestStatus = z.infer<typeof VantaTestStatusSchema>;

export const VantaIntegrationRefSchema = z
  .object({
    integrationId: z.string(),
    displayName: z.string().optional(),
  })
  .loose();

export const VantaFrameworkRefSchema = z
  .object({
    frameworkId: z.string(),
    displayName: z.string().optional(),
    controlIds: z.array(z.string()).optional(),
  })
  .loose();

function vantaTestAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const remediationInfo = isRawObject(raw.remediationStatusInfo) ? raw.remediationStatusInfo : null;
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, raw.displayName, rawIdString(raw.id)),
    status: pickFirst(raw.status) ?? null,
    // Real API: `integrations` is `string[]` of integration ids; legacy fixtures use `{integrationId}` objects.
    integrations: Array.isArray(raw.integrations) ? raw.integrations.map((i) => (typeof i === 'string' ? { integrationId: i } : i)) : [],
    frameworks: Array.isArray(raw.frameworks) ? raw.frameworks : [],
    remediation: pickFirst(raw.remediation, raw.remediationDescription) ?? null,
    failingEntityCount: pickFirst(raw.failingEntityCount, remediationInfo?.itemCount) ?? null,
  };
}

export const VantaTestSchema = z.preprocess(
  vantaTestAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      description: z.string().nullable().optional(),
      status: rawStatus,
      category: z.string().nullable().optional(),
      integrations: z.array(VantaIntegrationRefSchema).default([]),
      frameworks: z.array(VantaFrameworkRefSchema).default([]),
      remediation: z.string().nullable().optional(),
      failureDescription: z.string().nullable().optional(),
      latestFlipDate: isoDateTime,
      lastTestRunDate: isoDateTime,
      failingEntityCount: z.number().int().nonnegative().nullable().optional(),
      owner: z
        .object({ id: z.string(), displayName: z.string().optional(), emailAddress: z.string().optional() })
        .loose()
        .nullable()
        .optional(),
      deactivatedStatusInfo: z
        .object({
          isDeactivated: z.boolean(),
          deactivatedReason: z.string().nullable().optional(),
          lastUpdatedDate: isoDateTime,
        })
        .loose()
        .nullable()
        .optional(),
      remediationStatusInfo: z
        .object({ status: z.string().nullable().optional(), soonestRemediateByDate: isoDateTime, itemCount: z.number().int().nonnegative().nullable().optional() })
        .loose()
        .nullable()
        .optional(),
      testUrl: looseUrl,
    })
    .loose(),
);
export type VantaTest = z.infer<typeof VantaTestSchema>;

export const VANTA_ENTITY_STATUSES = ['FAILING', 'PASSING', 'DEACTIVATED', 'IGNORED'] as const;
export const VantaEntityStatusSchema = z.enum(VANTA_ENTITY_STATUSES);
export type VantaEntityStatus = z.infer<typeof VantaEntityStatusSchema>;

function vantaTestEntityAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  return {
    ...raw,
    id: rawIdString(raw.id),
    testId: rawIdString(raw.testId),
    displayName: pickFirst(raw.displayName, raw.name, rawIdString(raw.id)),
    status: pickFirst(raw.status, raw.entityStatus) ?? null,
    resourceType: pickFirst(raw.resourceType, raw.responseType) ?? 'unknown',
    resourceId: pickFirst(raw.resourceId, rawIdString(raw.id)),
    firstFailingAt: pickFirst(raw.firstFailingAt, raw.createdDate) ?? null,
  };
}

/** `testId` is not in the real payload; the adapter injects it from the request path. */
export const VantaTestEntitySchema = z.preprocess(
  vantaTestEntityAliases,
  z
    .object({
      id: z.string().min(1),
      testId: z.string().min(1),
      displayName: z.string(),
      status: rawStatus,
      resourceType: z.string(),
      integration: z.string().nullable().optional(),
      resourceId: z.string(),
      ownerPersonId: z.string().nullable().optional(),
      deactivatedReason: z.string().nullable().optional(),
      failureDescription: z.string().nullable().optional(),
      firstFailingAt: isoDateTime,
      lastUpdatedDate: isoDateTime,
      entityUrl: looseUrl,
    })
    .loose(),
);
export type VantaTestEntity = z.infer<typeof VantaTestEntitySchema>;

/** Legacy values plus the verified `uploadStatus` values (with spaces). */
export const VANTA_DOCUMENT_STATUSES = ['OK', 'NEEDS_ATTENTION', 'DUE_SOON', 'NOT_APPLICABLE', 'NOT_RELEVANT', 'Needs document', 'Needs update', 'Not relevant'] as const;
export const VantaDocumentStatusSchema = z.enum(VANTA_DOCUMENT_STATUSES);
export type VantaDocumentStatus = z.infer<typeof VantaDocumentStatusSchema>;

function vantaDocumentAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  return {
    ...raw,
    id: rawIdString(raw.id),
    title: pickFirst(raw.title, raw.name, rawIdString(raw.id)),
    status: pickFirst(raw.status, raw.uploadStatus) ?? null,
    frameworks: Array.isArray(raw.frameworks) ? raw.frameworks : [],
    updatedAt: pickFirst(raw.updatedAt, raw.uploadStatusDate) ?? null,
    isPolicy: typeof raw.isPolicy === 'boolean' ? raw.isPolicy : false,
    documentUrl: pickFirst(raw.documentUrl, raw.url) ?? null,
  };
}

export const VantaDocumentSchema = z.preprocess(
  vantaDocumentAliases,
  z
    .object({
      id: z.string().min(1),
      title: z.string(),
      description: z.string().nullable().optional(),
      status: rawStatus,
      frameworks: z.array(VantaFrameworkRefSchema).default([]),
      category: z.string().nullable().optional(),
      updatedAt: isoDateTime,
      isPolicy: z.boolean().default(false),
      isSensitive: z.boolean().nullable().optional(),
      ownerId: z.string().nullable().optional(),
      owner: z.object({ id: z.string(), displayName: z.string().optional() }).loose().nullable().optional(),
      documentUrl: looseUrl,
    })
    .loose(),
);
export type VantaDocument = z.infer<typeof VantaDocumentSchema>;

/** Legacy values plus the verified `status` values (`OK`, `NEEDS_REMEDIATION`). */
export const VANTA_POLICY_STATUSES = ['APPROVED', 'DRAFT', 'NEEDS_APPROVAL', 'EXPIRED', 'NOT_APPLICABLE', 'OK', 'NEEDS_REMEDIATION'] as const;
export const VantaPolicyStatusSchema = z.enum(VANTA_POLICY_STATUSES);
export type VantaPolicyStatus = z.infer<typeof VantaPolicyStatusSchema>;

/** Verified `latestVersion.status` values; refine the policy status downstream. */
export const VANTA_POLICY_VERSION_STATUSES = ['NOT_STARTED', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'RENEW_SOON', 'EXPIRED'] as const;

function vantaPolicyAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const approved = isRawObject(raw.latestApprovedVersion) ? raw.latestApprovedVersion : null;
  const documents = approved && Array.isArray(approved.documents) ? approved.documents : [];
  const firstDoc = documents.find(isRawObject);
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, raw.title, rawIdString(raw.id)),
    status: pickFirst(raw.status) ?? null,
    frameworks: Array.isArray(raw.frameworks) ? raw.frameworks : [],
    approvedAt: pickFirst(raw.approvedAt, raw.approvedAtDate) ?? null,
    policyUrl: pickFirst(raw.policyUrl, firstDoc?.url) ?? null,
  };
}

export const VantaPolicySchema = z.preprocess(
  vantaPolicyAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      description: z.string().nullable().optional(),
      status: rawStatus,
      latestVersion: z.object({ status: z.string().nullable().optional() }).loose().nullable().optional(),
      frameworks: z.array(VantaFrameworkRefSchema).default([]),
      approvedAt: isoDateTime,
      renewalDate: isoDateTime,
      updatedAt: isoDateTime,
      acceptanceCount: z.number().int().nonnegative().nullable().optional(),
      requiredAcceptanceCount: z.number().int().nonnegative().nullable().optional(),
      documentId: z.string().nullable().optional(),
      policyUrl: looseUrl,
    })
    .loose(),
);
export type VantaPolicy = z.infer<typeof VantaPolicySchema>;

const nullableCount = z.number().int().nonnegative().nullable();

export const VantaCompletionStatusSchema = z
  .object({
    controlsPassing: nullableCount,
    controlsTotal: nullableCount,
    testsPassing: nullableCount,
    testsTotal: nullableCount,
    documentsPassing: nullableCount,
    documentsTotal: nullableCount,
  })
  .loose();
export type VantaCompletionStatus = z.infer<typeof VantaCompletionStatusSchema>;

function countOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;
}

function vantaFrameworkAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const legacy = isRawObject(raw.completionStatus) ? raw.completionStatus : {};
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, raw.displayName, raw.shorthandName, rawIdString(raw.id)),
    completionStatus: {
      controlsPassing: countOrNull(pickFirst(legacy.controlsPassing, raw.numControlsCompleted)),
      controlsTotal: countOrNull(pickFirst(legacy.controlsTotal, raw.numControlsTotal)),
      testsPassing: countOrNull(pickFirst(legacy.testsPassing, raw.numTestsPassing)),
      testsTotal: countOrNull(pickFirst(legacy.testsTotal, raw.numTestsTotal)),
      documentsPassing: countOrNull(pickFirst(legacy.documentsPassing, raw.numDocumentsPassing)),
      documentsTotal: countOrNull(pickFirst(legacy.documentsTotal, raw.numDocumentsTotal)),
    },
  };
}

export const VantaFrameworkSchema = z.preprocess(
  vantaFrameworkAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      displayName: z.string().nullable().optional(),
      shorthandName: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      completionStatus: VantaCompletionStatusSchema,
      frameworkUrl: looseUrl,
    })
    .loose(),
);
export type VantaFramework = z.infer<typeof VantaFrameworkSchema>;

function vantaControlAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, raw.externalId, rawIdString(raw.id)),
    testIds: stringArray(raw.testIds),
    documentIds: stringArray(raw.documentIds),
    status: pickFirst(raw.status) ?? undefined,
  };
}

/** Vanta exposes no control status; readiness is derived from the optional test/document child lists. */
export const VantaFrameworkControlSchema = z.preprocess(
  vantaControlAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      description: z.string().nullable().optional(),
      externalId: z.string().nullable().optional(),
      frameworkId: z.string().optional(),
      source: z.string().nullable().optional(),
      domains: z.array(z.string()).optional(),
      testIds: z.array(z.string()).default([]),
      documentIds: z.array(z.string()).default([]),
      status: z.string().optional(),
    })
    .loose(),
);
export type VantaFrameworkControl = z.infer<typeof VantaFrameworkControlSchema>;

export const VANTA_SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
export const VantaSeveritySchema = z.enum(VANTA_SEVERITIES);
export type VantaSeverity = z.infer<typeof VantaSeveritySchema>;

function vantaVulnerabilityAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const deactivate = isRawObject(raw.deactivateMetadata) ? raw.deactivateMetadata : null;
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, raw.externalId, rawIdString(raw.id)),
    severity: typeof raw.severity === 'string' ? raw.severity.toUpperCase() : null,
    isFixable: typeof raw.isFixable === 'boolean' ? raw.isFixable : null,
    assetId: pickFirst(raw.assetId, raw.targetId) ?? '',
    assetName: pickFirst(raw.assetName, raw.targetId, raw.assetId) ?? '',
    source: pickFirst(raw.source, raw.scanSource, raw.integrationId) ?? 'unknown',
    externalVulnerabilityId: pickFirst(raw.externalVulnerabilityId, raw.externalId) ?? null,
    firstDetectedAt: pickFirst(raw.firstDetectedAt, raw.firstDetectedDate, raw.sourceDetectedDate) ?? null,
    lastDetectedAt: pickFirst(raw.lastDetectedAt, raw.lastDetectedDate) ?? null,
    deactivatedAt: pickFirst(raw.deactivatedAt, deactivate?.deactivatedOnDate) ?? null,
    externalUrl: pickFirst(raw.externalUrl, raw.externalURL) ?? null,
  };
}

export const VantaVulnerabilitySchema = z.preprocess(
  vantaVulnerabilityAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      description: z.string().nullable().optional(),
      packageName: z.string().nullable().optional(),
      packageIdentifier: z.string().nullable().optional(),
      fixedVersion: z.string().nullable().optional(),
      installedVersion: z.string().nullable().optional(),
      severity: rawStatus,
      cvssSeverityScore: z.number().nullable().optional(),
      remediateByDate: isoDateTime,
      isFixable: z.boolean().nullable(),
      assetId: z.string(),
      assetName: z.string(),
      source: z.string(),
      vulnerabilityType: z.string().nullable().optional(),
      externalVulnerabilityId: z.string().nullable().optional(),
      firstDetectedAt: isoDateTime,
      lastDetectedAt: isoDateTime,
      deactivatedAt: isoDateTime,
      /** Real API: non-null when the finding is deactivated (with or without a date). */
      deactivateMetadata: z.object({}).loose().nullable().optional(),
      externalUrl: looseUrl,
    })
    .loose(),
);
export type VantaVulnerability = z.infer<typeof VantaVulnerabilitySchema>;

export const VANTA_EMPLOYMENT_STATUSES = [
  'CURRENT',
  'FORMER',
  'FUTURE_HIRE',
  'CONTRACTOR',
  'OUT_OF_SCOPE',
  'SERVICE_ACCOUNT',
] as const;
export const VantaEmploymentStatusSchema = z.enum(VANTA_EMPLOYMENT_STATUSES);
export type VantaEmploymentStatus = z.infer<typeof VantaEmploymentStatusSchema>;

/** real-providers change: verified `employment.status` values of the Manage API (the fixtures keep the legacy list above). */
export const VANTA_REAL_EMPLOYMENT_STATUSES = ['UPCOMING', 'CURRENT', 'ON_LEAVE', 'INACTIVE', 'FORMER'] as const;

/** Verified `tasksSummary.status` and per-task `status` values. */
export const VANTA_TASK_SUMMARY_STATUSES = ['COMPLETE', 'DUE_SOON', 'NONE', 'OVERDUE', 'PAUSED', 'OFFBOARDING_COMPLETE', 'OFFBOARDING_DUE_SOON', 'OFFBOARDING_OVERDUE', 'INCOMPLETE', 'NOT_APPLICABLE'] as const;

/** Task summary flags: absent/null means the provider did not report the check. */
export const VantaTasksSummarySchema = z
  .object({
    policiesAccepted: nullableBool,
    securityTrainingCompleted: nullableBool,
    hipaaTrainingCompleted: nullableBool,
    aiTrainingCompleted: nullableBool,
    backgroundCheckCompleted: nullableBool,
    mfaEnabled: nullableBool,
    deviceMonitoringInstalled: nullableBool,
    onboardingUrl: looseUrl,
    completionDate: isoDateTime,
    dueDate: isoDateTime,
    status: z.string().nullable().optional(),
  })
  .loose();
export type VantaTasksSummary = z.infer<typeof VantaTasksSummarySchema>;

/** Real task detail status → tri-state flag: COMPLETE true, OVERDUE/DUE_SOON false, NONE/disabled/absent null. */
function taskFlag(detail: unknown): boolean | null {
  if (!isRawObject(detail)) return null;
  if (isRawObject(detail.disabled) || detail.disabled === true) return null;
  const status = detail.status;
  if (status === 'COMPLETE') return true;
  if (status === 'OVERDUE' || status === 'DUE_SOON') return false;
  return null;
}

function vantaTasksSummaryAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const details = isRawObject(raw.details) ? raw.details : null;
  if (!details) return raw;
  return {
    ...raw,
    policiesAccepted: pickFirst(raw.policiesAccepted, taskFlag(details.acceptPolicies)) ?? null,
    securityTrainingCompleted: pickFirst(raw.securityTrainingCompleted, taskFlag(details.completeTrainings)) ?? null,
    backgroundCheckCompleted: pickFirst(raw.backgroundCheckCompleted, taskFlag(details.completeBackgroundChecks)) ?? null,
    deviceMonitoringInstalled: pickFirst(raw.deviceMonitoringInstalled, taskFlag(details.installDeviceMonitoring)) ?? null,
  };
}

function vantaPersonAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const name = isRawObject(raw.name) ? raw.name : null;
  const employment = isRawObject(raw.employment) ? raw.employment : null;
  const composed = name ? [name.first, name.last].filter((s): s is string => typeof s === 'string' && s.trim() !== '').join(' ') : '';
  const emailAddress = pickFirst(raw.emailAddress, raw.email) ?? '';
  return {
    ...raw,
    id: rawIdString(raw.id),
    emailAddress,
    displayName: pickFirst(raw.displayName, name?.display, composed || undefined, emailAddress || undefined, rawIdString(raw.id)),
    employmentStatus: pickFirst(raw.employmentStatus, employment?.status) ?? null,
    startDate: pickFirst(raw.startDate, employment?.startDate) ?? null,
    endDate: pickFirst(raw.endDate, employment?.endDate) ?? null,
    groupIds: stringArray(raw.groupIds),
    computerIds: stringArray(raw.computerIds),
    tasksSummary: isRawObject(raw.tasksSummary) ? vantaTasksSummaryAliases(raw.tasksSummary) : raw.tasksSummary ?? null,
    sources: Array.isArray(raw.sources) ? raw.sources.filter((s): s is string => typeof s === 'string') : undefined,
  };
}

export const VantaPersonSchema = z.preprocess(
  vantaPersonAliases,
  z
    .object({
      id: z.string().min(1),
      emailAddress: z.string(),
      displayName: z.string(),
      employmentStatus: rawStatus,
      startDate: isoDateTime,
      endDate: isoDateTime,
      groupIds: z.array(z.string()).default([]),
      tasksSummary: VantaTasksSummarySchema.nullable().optional(),
      computerIds: z.array(z.string()).default([]),
      sources: z.array(z.string()).optional(),
    })
    .loose(),
);
export type VantaPerson = z.infer<typeof VantaPersonSchema>;

/** Real `{outcome: PASS|FAIL|IN_PROGRESS|NA}` objects → tri-state booleans; legacy booleans pass through. */
function outcomeFlag(v: unknown): boolean | null | undefined {
  if (typeof v === 'boolean' || v === null || v === undefined) return v;
  if (!isRawObject(v)) return null;
  if (v.outcome === 'PASS') return true;
  if (v.outcome === 'FAIL') return false;
  return null;
}

function vantaComputerAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const owner = isRawObject(raw.owner) ? raw.owner : null;
  const os = isRawObject(raw.operatingSystem) ? raw.operatingSystem : null;
  const osLabel = os ? [os.type, os.version].filter((s): s is string => typeof s === 'string' && s !== '').join(' ') : '';
  return {
    ...raw,
    id: rawIdString(raw.id),
    ownerId: pickFirst(raw.ownerId, owner?.id) ?? null,
    osVersion: pickFirst(raw.osVersion, osLabel || undefined) ?? null,
    passwordManager: outcomeFlag(raw.passwordManager),
    diskEncryption: outcomeFlag(raw.diskEncryption),
    antivirus: outcomeFlag(pickFirst(raw.antivirus, raw.antivirusInstallation)),
    screenLock: outcomeFlag(pickFirst(raw.screenLock, raw.screenlock)),
    autoUpdates: outcomeFlag(raw.autoUpdates),
    agentInstalled: outcomeFlag(raw.agentInstalled),
    lastCheckIn: pickFirst(raw.lastCheckIn, raw.lastCheckDate) ?? null,
  };
}

export const VantaMonitoredComputerSchema = z.preprocess(
  vantaComputerAliases,
  z
    .object({
      id: z.string().min(1),
      ownerId: z.string().nullable().optional(),
      serialNumber: z.string().nullable().optional(),
      udid: z.string().nullable().optional(),
      integrationId: z.string().nullable().optional(),
      hostname: z.string().nullable().optional(),
      osVersion: z.string().nullable().optional(),
      agentInstalled: nullableBool,
      agentVersion: z.string().nullable().optional(),
      passwordManager: nullableBool,
      diskEncryption: nullableBool,
      antivirus: nullableBool,
      autoUpdates: nullableBool,
      screenLock: nullableBool,
      lastCheckIn: isoDateTime,
      isActive: nullableBool,
    })
    .loose(),
);
export type VantaMonitoredComputer = z.infer<typeof VantaMonitoredComputerSchema>;

/** Derive a connection status from the verified `connections[]` list. */
function integrationStatusFromConnections(connections: unknown): string | null {
  if (!Array.isArray(connections)) return null;
  const list = connections.filter(isRawObject);
  if (list.length === 0) return 'NO_CONNECTIONS';
  if (list.some((c) => typeof c.connectionErrorMessage === 'string' && c.connectionErrorMessage !== '')) return 'ERROR';
  if (list.every((c) => c.isDisabled === true)) return 'DISABLED';
  return 'CONNECTED';
}

function vantaIntegrationAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const integrationId = pickFirst(raw.integrationId, rawIdString(raw.id));
  return {
    ...raw,
    id: rawIdString(pickFirst(raw.id, raw.integrationId)),
    integrationId: rawIdString(integrationId) ?? '',
    displayName: pickFirst(raw.displayName, raw.name, rawIdString(integrationId)) ?? '',
    connectionStatus: pickFirst(raw.connectionStatus, integrationStatusFromConnections(raw.connections)) ?? null,
  };
}

export const VantaIntegrationSchema = z.preprocess(
  vantaIntegrationAliases,
  z
    .object({
      id: z.string().min(1),
      integrationId: z.string(),
      displayName: z.string(),
      connectionStatus: rawStatus,
      connectedAt: isoDateTime,
      lastSyncedAt: isoDateTime,
      category: z.string().nullable().optional(),
      resourceKinds: z.array(z.string()).optional(),
      connections: z.array(z.object({ connectionId: z.string().optional(), isDisabled: z.boolean().optional(), connectionErrorMessage: z.string().nullable().optional() }).loose()).optional(),
    })
    .loose(),
);
export type VantaIntegration = z.infer<typeof VantaIntegrationSchema>;

function vantaVendorAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const category = isRawObject(raw.category) ? raw.category.displayName : raw.category;
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, rawIdString(raw.id)),
    category: typeof category === 'string' ? category : null,
    riskLevel: pickFirst(raw.riskLevel, raw.residualRiskLevel, raw.inherentRiskLevel) ?? null,
    status: pickFirst(raw.status) ?? null,
    ownerId: pickFirst(raw.ownerId, raw.securityOwnerUserId, raw.businessOwnerUserId) ?? null,
    lastReviewedAt: pickFirst(raw.lastReviewedAt, raw.lastSecurityReviewCompletionDate) ?? null,
    nextReviewAt: pickFirst(raw.nextReviewAt, raw.nextSecurityReviewDueDate) ?? null,
  };
}

export const VantaVendorSchema = z.preprocess(
  vantaVendorAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      description: z.string().nullable().optional(),
      category: z.string().nullable().optional(),
      riskLevel: z.string().nullable().optional(),
      status: rawStatus,
      ownerId: z.string().nullable().optional(),
      websiteUrl: z.string().nullable().optional(),
      lastReviewedAt: isoDateTime,
      nextReviewAt: isoDateTime,
    })
    .loose(),
);
export type VantaVendor = z.infer<typeof VantaVendorSchema>;

export const VantaTestsPageSchema = vantaPage(VantaTestSchema);
export const VantaTestEntitiesPageSchema = vantaPage(VantaTestEntitySchema);
export const VantaDocumentsPageSchema = vantaPage(VantaDocumentSchema);
export const VantaPoliciesPageSchema = vantaPage(VantaPolicySchema);
export const VantaFrameworksPageSchema = vantaPage(VantaFrameworkSchema);
export const VantaFrameworkControlsPageSchema = vantaPage(VantaFrameworkControlSchema);
export const VantaVulnerabilitiesPageSchema = vantaPage(VantaVulnerabilitySchema);
export const VantaPeoplePageSchema = vantaPage(VantaPersonSchema);
export const VantaMonitoredComputersPageSchema = vantaPage(VantaMonitoredComputerSchema);
export const VantaIntegrationsPageSchema = vantaPage(VantaIntegrationSchema);
export const VantaVendorsPageSchema = vantaPage(VantaVendorSchema);

/** Endpoint templates (relative to the API base URL). Used for adapters, sim routes and audit details. */
export const VANTA_ENDPOINTS = {
  token: '/oauth/token',
  tests: '/v1/tests',
  testEntities: '/v1/tests/{testId}/entities',
  documents: '/v1/documents',
  policies: '/v1/policies',
  frameworks: '/v1/frameworks',
  frameworkControls: '/v1/frameworks/{frameworkId}/controls',
  controlTests: '/v1/controls/{controlId}/tests',
  controlDocuments: '/v1/controls/{controlId}/documents',
  documentControls: '/v1/documents/{documentId}/controls',
  vulnerabilities: '/v1/vulnerabilities',
  vulnerabilityRemediations: '/v1/vulnerability-remediations',
  people: '/v1/people',
  monitoredComputers: '/v1/monitored-computers',
  integrations: '/v1/integrations',
  vendors: '/v1/vendors',
} as const;
export type VantaEndpoint = (typeof VANTA_ENDPOINTS)[keyof typeof VANTA_ENDPOINTS];

/** Documented limits (spec section 6): 50 Manage requests/min, 5 OAuth requests/min, ceiling 40. */
export const VANTA_LIMITS = {
  documentedRequestsPerMinute: 50,
  oauthRequestsPerMinute: 5,
  defaultCeilingPerMinute: 40,
  maxPageSize: 100,
} as const;

// ---------------------------------------------------------------------------------------------
// real-providers change: verified hosts (PROVIDER_API_NOTES section 1.1)
// ---------------------------------------------------------------------------------------------

/** The only API host; the regional `api.eu` / `api.aus` hosts answer 308 to it. Gov tenants use their own host. */
export const VANTA_API_BASE_URL = 'https://api.vanta.com' as const;
export const VANTA_GOV_API_BASE_URL = 'https://api.vanta-gov.com' as const;
export const VANTA_API_HOSTS: readonly string[] = ['api.vanta.com', 'api.vanta-gov.com'];
/** Browser (app) hosts accepted for the optional tenant browser URL. */
export const VANTA_BROWSER_HOSTS: readonly string[] = ['app.vanta.com', 'app.eu.vanta.com', 'app.aus.vanta.com', 'app.vanta-gov.com'];
export const VANTA_OAUTH_SCOPE_READ = 'vanta-api.all:read' as const;

/** The tenant a Vanta browser URL points at: its app host and the customer slug. */
export interface VantaTenantLocation {
  readonly host: string;
  readonly slug: string;
}

/**
 * Reads the tenant slug from the optional tenant browser URL: `https://app.vanta.com/c/<slug>/...`
 * (the in-app form) or the older `https://app.vanta.com/<slug>`. The slug is fixed when the tenant is
 * created and often differs from the company's current name, so it can only come from the URL the
 * operator pasted. Null for other hosts, http, or a URL without a slug.
 */
export function vantaTenantFromBrowserUrl(browserUrl: string | null | undefined): VantaTenantLocation | null {
  if (!browserUrl) return null;
  let url: URL;
  try {
    url = new URL(browserUrl);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (url.protocol !== 'https:' || url.username || url.password || !VANTA_BROWSER_HOSTS.includes(host)) return null;
  const parts = url.pathname.split('/').filter(Boolean);
  const slug = parts[0] === 'c' ? parts[1] : parts[0];
  if (!slug || !/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(slug)) return null;
  return { host, slug };
}

/**
 * Path segment of each linkable record type inside a tenant. The Vanta app routes tests at
 * `/tests/<id>` and documents at `/documents/<id>` (plural, like the top-level `/tests` and
 * `/documents` pages); the singular forms are not routes and land on a blank page.
 */
export const VANTA_RECORD_PATH_SEGMENTS = { test: 'tests', document: 'documents' } as const;
export type VantaLinkableRecordKind = keyof typeof VANTA_RECORD_PATH_SEGMENTS;

/** Deep link to one test or document in the Vanta app: `https://app.vanta.com/c/<slug>/<tests|documents>/<id>`. */
export function vantaRecordUrl(tenant: VantaTenantLocation, kind: VantaLinkableRecordKind, id: string): string {
  return `https://${tenant.host}/c/${encodeURIComponent(tenant.slug)}/${VANTA_RECORD_PATH_SEGMENTS[kind]}/${encodeURIComponent(id)}`;
}

/**
 * Repairs a deep link built by earlier builds, which used the singular `/c/<slug>/test/<id>` and
 * `/c/<slug>/document/<id>` paths. Returns the plural-path URL, or null when the URL is not a Vanta
 * app link of that shape (including links that are already correct).
 */
export function repairVantaRecordUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password || !VANTA_BROWSER_HOSTS.includes(parsed.hostname.toLowerCase())) return null;
  const match = /^\/c\/([^/]+)\/(test|document)\/([^/]+)$/.exec(parsed.pathname);
  if (!match) return null;
  parsed.pathname = `/c/${match[1]}/${VANTA_RECORD_PATH_SEGMENTS[match[2] as VantaLinkableRecordKind]}/${match[3]}`;
  return parsed.toString();
}

// ---------------------------------------------------------------------------------------------
// added by sim-providers: tenant identity (discovery) and vulnerability remediations
// ---------------------------------------------------------------------------------------------

/**
 * Endpoint the adapter probes after minting a token. real-providers change: the real Manage API has
 * no tenant endpoint (404); the probe is optional and a 404 falls back to the client-id hash.
 */
export const VANTA_IDENTITY_ENDPOINT = '/v1/me' as const; // added by sim-providers

/** Tenant identity used for the tenant-mismatch guard (spec section 5). */
export const VantaTenantIdentitySchema = z // added by sim-providers
  .object({
    organizationId: z.string().min(1),
    organizationName: z.string(),
    domain: z.string().nullable().optional(),
    browserUrl: looseUrl,
    scopes: z.array(z.string()).default([]),
    tokenExpiresAt: isoDateTime,
  })
  .loose();
export type VantaTenantIdentity = z.infer<typeof VantaTenantIdentitySchema>;

export const VANTA_VULNERABILITY_REMEDIATION_STATUSES = ['OPEN', 'IN_PROGRESS', 'REMEDIATED', 'DEACTIVATED'] as const; // added by sim-providers
export const VantaVulnerabilityRemediationStatusSchema = z.enum(VANTA_VULNERABILITY_REMEDIATION_STATUSES);
export type VantaVulnerabilityRemediationStatus = z.infer<typeof VantaVulnerabilityRemediationStatusSchema>;

function vantaRemediationAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const remediated = typeof raw.remediationDate === 'string' && raw.remediationDate !== '';
  return {
    ...raw,
    id: rawIdString(raw.id),
    vulnerabilityId: rawIdString(raw.vulnerabilityId),
    vulnerabilityIds: stringArray(raw.vulnerabilityIds),
    assetId: pickFirst(raw.assetId, raw.vulnerableAssetId) ?? '',
    assetName: pickFirst(raw.assetName, raw.vulnerableAssetId, raw.assetId) ?? '',
    severity: typeof raw.severity === 'string' ? raw.severity.toUpperCase() : null,
    status: pickFirst(raw.status, remediated ? 'REMEDIATED' : undefined) ?? 'OPEN',
    isFixable: typeof raw.isFixable === 'boolean' ? raw.isFixable : null,
    remediateByDate: pickFirst(raw.remediateByDate, raw.slaDeadlineDate) ?? null,
    remediatedAt: pickFirst(raw.remediatedAt, raw.remediationDate) ?? null,
    source: pickFirst(raw.source, raw.integrationId) ?? 'unknown',
    firstDetectedAt: pickFirst(raw.firstDetectedAt, raw.detectedDate) ?? null,
  };
}

/** One remediation row per vulnerability finding (GET /v1/vulnerability-remediations). */
export const VantaVulnerabilityRemediationSchema = z.preprocess(
  vantaRemediationAliases, // added by sim-providers; real-providers change: tolerant aliases
  z
    .object({
      id: z.string().min(1),
      vulnerabilityId: z.string().min(1),
      vulnerabilityIds: z.array(z.string()).default([]),
      assetId: z.string(),
      assetName: z.string(),
      packageName: z.string().nullable().optional(),
      packageIdentifier: z.string().nullable().optional(),
      fixedVersion: z.string().nullable().optional(),
      installedVersion: z.string().nullable().optional(),
      severity: rawStatus,
      status: z.string(),
      isFixable: z.boolean().nullable(),
      remediateByDate: isoDateTime,
      remediatedAt: isoDateTime,
      source: z.string(),
      firstDetectedAt: isoDateTime,
    })
    .loose(),
);
export type VantaVulnerabilityRemediation = z.infer<typeof VantaVulnerabilityRemediationSchema>;
export const VantaVulnerabilityRemediationsPageSchema = vantaPage(VantaVulnerabilityRemediationSchema); // added by sim-providers
